/**
 * Appointment Reminder Popup Component
 * Shows a notification 15 minutes before virtual appointments
 * Displays appointment timezone and allows navigation to patient details
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Calendar, MapPin, User, X, Bell } from 'lucide-react';
import { ScheduledAppointment } from '@/services/features/ScheduledAppointmentsService';
import { toZonedTime, format } from 'date-fns-tz';

interface AppointmentReminderPopupProps {
  isOpen: boolean;
  onClose: () => void;
  appointments: ScheduledAppointment[];
}

export const AppointmentReminderPopup: React.FC<AppointmentReminderPopupProps> = ({
  isOpen,
  onClose,
  appointments,
}) => {
  const navigate = useNavigate();

  const handlePatientClick = (enrollmentPatientId: number | null) => {
    if (enrollmentPatientId) {
      onClose();
      navigate(`/es-dashboard?from=global-search&patientId=${enrollmentPatientId}`);
    }
  };

  const formatDateTime = (dateTimeString: string, timezone: string) => {
    try {
      const utcDate = new Date(dateTimeString);
      const zonedDate = toZonedTime(utcDate, timezone);
      
      const formattedDate = format(zonedDate, 'MMM dd, yyyy', { timeZone: timezone });
      const formattedTime = format(zonedDate, 'hh:mm a', { timeZone: timezone });
      
      return { date: formattedDate, time: formattedTime };
    } catch {
      return { date: '-', time: '-' };
    }
  };

  const getMinutesUntilAppointment = (dateTimeString: string, timezone: string): number => {
    try {
      const utcDate = new Date(dateTimeString);
      const now = new Date();
      const diffMs = utcDate.getTime() - now.getTime();
      return Math.floor(diffMs / (1000 * 60));
    } catch {
      return 0;
    }
  };

  if (appointments.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        id="appointment-reminder-popup"
        className="sm:max-w-lg max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Bell className="h-5 w-5 animate-pulse" />
            Upcoming Appointment Reminder
          </DialogTitle>
          <DialogDescription>
            You have {appointments.length} virtual appointment{appointments.length > 1 ? 's' : ''} starting soon!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {appointments.map((appointment) => {
            const timezone = appointment.clinic_timezone || 'UTC';
            const startDateTime = formatDateTime(appointment.appointment_date_start_time, timezone);
            const endDateTime = formatDateTime(appointment.appointment_date_end_time, timezone);
            const minutesUntil = getMinutesUntilAppointment(appointment.appointment_date_start_time, timezone);

            return (
              <Card 
                key={appointment.appointment_id}
                id={`reminder-card-${appointment.appointment_id}`}
                className="border border-primary/20 bg-primary/5"
              >
                <CardContent className="p-4 space-y-3">
                  {/* Time remaining badge */}
                  <div className="flex justify-between items-start">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      <Clock className="h-3 w-3" />
                      {minutesUntil > 0 ? `Starts in ${minutesUntil} min` : 'Starting now'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {timezone}
                    </span>
                  </div>

                  {/* Patient Name - Clickable */}
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Patient:</span>
                    {appointment.enrollment_patient_id ? (
                      <button
                        id={`reminder-patient-link-${appointment.appointment_id}`}
                        onClick={() => handlePatientClick(appointment.enrollment_patient_id)}
                        className="text-sm font-medium text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
                      >
                        {appointment.patient_name || 'Unknown Patient'}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-foreground">
                        {appointment.patient_name || 'Unknown Patient'}
                      </span>
                    )}
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Date:</span>
                    <span className="text-sm font-medium text-foreground">
                      {startDateTime.date}
                    </span>
                  </div>

                  {/* Time */}
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Time:</span>
                    <span className="text-sm font-medium text-foreground">
                      {startDateTime.time} - {endDateTime.time}
                    </span>
                  </div>

                  {/* Clinic */}
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Clinic:</span>
                    <span className="text-sm font-medium text-foreground">
                      {appointment.clinic_name || '-'}
                    </span>
                  </div>

                  {/* User/Resource */}
                  {appointment.user_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">With:</span>
                      <span className="text-sm font-medium text-foreground">
                        {appointment.user_name}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Close Button */}
        <div className="flex justify-end mt-4">
          <Button
            id="appointment-reminder-close-btn"
            onClick={onClose}
            variant="outline"
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
